<template>
  <div :class="$style.recordBlock">
    <div>
      <div
        :class="$style.articleContent"
        ref="articleContent"
        :data-io-article-url="pageUrl"
        data-test="app-article-content"
      >
        <record-header
          id="record-header"
          :is-show-content="isBroadcast || isCards"
          :is-actions-shown="!isMobile"
          @click.native="handleHeaderClick($event)"
        />

        <div :class="$style.articleContentText">
          <broadcast-content
            v-if="isBroadcast"
            :key="recordData.id"
            :page-url="pageUrl"
            itemprop="articleBody"
            @click.native="handleClick($event)"
            @sortedPosts="sendClickAnalytics"
          />

          <cards-content
            v-else-if="isCards"
            :page-url="pageUrl"
            itemprop="articleBody"
            @click.native="handleClick($event)"
          />

          <ContentBlocksList
            v-else
            :page-url="pageUrl"
            :data="recordData.text"
            itemprop="articleBody"
            @click.native="handleClick($event)"
          />

          <template v-for="(checkPoint, index) of checkPointList">
            <div
              v-observe-visibility="checkPointObsOptions(checkPoint)"
              :key="`check-point-${index}`"
              :class="$style.checkPoint"
              :style="`top:${checkPoint}%`"
            />
          </template>
        </div>
      </div>

      <jtn-ui-record-text
        v-if="recordData.sourceAndAuthors"
        v-observe-visibility="nonIndexedObs.getOptions('all')"
        :text="recordData.sourceAndAuthors"
        @click.native="handleNonIndexedClick"
      />

      <record-attentions
        v-if="isShowAttention"
        :is-baa="isBaa"
        :is-contraindications="isContraindications"
      />

      <record-advertising-info
        v-if="recordData.isCommercial"
        :data="recordData.commercialLabel"
      />

      <record-forum-button v-if="recordForumBlock" :title="recordForumBlock.title" />

      <figure
        itemscope="itemscope"
        itemtype="http://schema.org/ImageObject"
        itemprop="image"
      >
        <link itemprop="url" :href="schemeImageUrl" />
        <link itemprop="contentUrl" :href="schemeImageUrl" />
      </figure>
    </div>
  </div>
</template>

<script>
import { StoreMixin, getImageData, ObserverVisibility } from '@jtnews/shared';
import { OPINION_RULE_LINK } from '@jtnews/shared/data';
import { JtnUiRecordText } from '@jtn-ui/components/record';
import {
  guardEmptyArray,
  guardUnspecified,
  guardEmptyString
} from '@portal/utils/util-guards';
import { useModule } from 'vuex-simple';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import RecordHeader from '../../../shared/components/_record/record-header/index';
import { RecordAttentions, RecordAdvertisingInfo } from '../components';
import { RECORD_MODULE_NAMESPACE } from '../store';

import BroadcastContent from './broadcast-content';
import CardsContent from './cards-content';
import ContentBlocksList from '../containers/content-blocks-list';
import { RecordForumButton } from './record-forum-button';
import { Adv } from '../../../shared/components/_adv';

const DEFAULT_FIELD_TYPE = 'По теме (блок)';

const VOTES_TYPES = {
  imagesPoll: 'С картинками',
  poll: 'Обычный',
  yesnotest: 'Да-Нет',
  test: 'Тест'
};

export default {
  name: 'RecordBlock',
  components: {
    BroadcastContent,
    CardsContent,
    ContentBlocksList,
    RecordHeader,
    RecordAdvertisingInfo,
    RecordForumButton,
    JtnUiRecordText,
    RecordAttentions,
    Adv
  },
  mixins: [StoreMixin],
  data() {
    return {
      pageUrl: '',
      isCheckReadingArticle: false,
      newArticleScrollPosition: null,
      oldArticleScrollPosition: null,
      notRefusalTimeout: null,
      delayCheckNotRefusal: 15000,
      opinionRuleLink: OPINION_RULE_LINK,
      votesViewTypes: {},
      isClickedRelatedNewsCounter: false,
      checkPointList: [25, 50, 75, 100],
      destroy$: new Subject(),
      nonIndexedObs: new ObserverVisibility('all')
    };
  },
  computed: {
    recordModule() {
      return useModule(this.$store, RECORD_MODULE_NAMESPACE);
    },
    userInfo() {
      return this.store.commonModule.userInfo;
    },
    recordForumBlock() {
      return this.store.layoutModule.recordForum;
    },
    regionId() {
      return this.store.regionId;
    },
    recordData() {
      const recordBlock = this.store.layoutModule.record;
      return recordBlock && recordBlock.data ? recordBlock.data : {};
    },
    isBroadcast() {
      return this.recordData && this.recordData.template === 'online';
    },
    isOpinion() {
      return this.recordData && this.recordData.template === 'opinions';
    },
    isCards() {
      return this.recordData && this.recordData.template === 'cards';
    },
    isCommercial() {
      return this.recordData.isCommercial;
    },
    isBaa() {
      return this.recordData?.isBaa;
    },
    isContraindications() {
      return this.recordData?.isContraindications;
    },
    isShowAttention() {
      return this.isBaa || this.isContraindications;
    },
    isMobile() {
      return this.store.deviceInfo.isMobile;
    },
    socialShareImage() {
      return this.recordData?.socialShare?.[0]?.image ?? '';
    },
    schemeImageUrl() {
      if (guardEmptyString(this.socialShareImage)) {
        return this.socialShareImage;
      }

      const data = this.recordData.text;

      if (
        guardEmptyArray(data) &&
        data[0].type === 'images' &&
        guardUnspecified(data[0]?.media?.class) &&
        data[0].media.class !== 'center-vertical'
      ) {
        return getImageData(data[0].value, this.isTablet, this.isMobile).src;
      }

      return '';
    },
    analyticRecordFormat() {
      return this.recordData.formats[0]?.name || this.recordData.analyticsType;
    },
    sourceAndAuthors() {
      return this.recordData.sourceAndAuthors || '';
    },
    isNonIdexedWithLink() {
      return this.sourceAndAuthors.toLowerCase().includes('href');
    }
  },
  watch: {
    userInfo(value) {
      if (value !== null) {
        this.store.userModule.sendRecordToBrowsingHistory({
          profileId: Number(value.userId),
          recordId: this.recordData.id
        });
      }
    }
  },
  mounted() {
    this.updatePageUrl();
    this.addScrollEvent();

    this.checkOpenArticle();

    this.$nextTick(() => {
      this.pushRecordHeight();
    });

    window.addEventListener('resize', this.pushRecordHeight);

    this.nonIndexedObs.event.pipe(takeUntil(this.destroy$)).subscribe(({ value }) => {
      this.nonIndexedVisible(value);
    });
  },
  updated() {
    this.updatePageUrl();
    if (!this.isCheckReadingArticle) {
      this.addScrollEvent();
    }
    this.pushRecordHeight();
    this.sendVotesView();
  },
  beforeDestroy() {
    this.removeScrollEvent();
    window.removeEventListener('resize', this.pushRecordHeight);
    if (this.notRefusalTimeout !== null) {
      clearTimeout(this.notRefusalTimeout);
    }
    this.destroy$.next();
    this.destroy$.unsubscribe();
  },
  methods: {
    sendClickAnalytics(clickPlace) {
      this.sendNewFormatReachGoal(
        'ClickOnlineMessage',
        'Блок онлайна',
        'Клик',
        clickPlace
      );
    },
    updatePageUrl() {
      if (typeof window !== 'undefined') {
        this.pageUrl = window.location.href;
      }
    },
    addScrollEvent() {
      this.isCheckReadingArticle = true;
      window.addEventListener('scroll', this.onScrollArticleContent);
    },
    removeScrollEvent() {
      this.isCheckReadingArticle = false;
      window.removeEventListener('scroll', this.onScrollArticleContent);
    },
    onScrollArticleContent() {
      if (!this.$refs.articleContent) {
        return;
      }

      const articlePositionParams = this.$refs.articleContent.getBoundingClientRect();
      this.newArticleScrollPosition =
        articlePositionParams.top + articlePositionParams.height - window.innerHeight;

      if (this.oldArticleScrollPosition > 0 && this.newArticleScrollPosition <= 0) {
        this.removeScrollEvent();
      }

      this.oldArticleScrollPosition = this.newArticleScrollPosition;
    },
    pushRecordHeight() {
      const recordHeight = this.$el.getBoundingClientRect().height;
      this.store.layoutModule.setRecordHeight(recordHeight);
    },
    setVotesViewTypes(attaches) {
      if (attaches && attaches.length) {
        attaches.forEach(({ type }) => {
          const votesTypesValue = VOTES_TYPES[type];

          if (votesTypesValue) {
            this.votesViewTypes[type] = votesTypesValue;
          }
        });
      }
    },
    sendVotesView() {
      this.setVotesViewTypes(this.recordData.text);

      if (this.recordData.cards && this.recordData.cards.length) {
        this.recordData.cards.forEach(({ data }) => this.setVotesViewTypes(data));
      }

      if (this.recordData.posts && this.recordData.posts.length) {
        this.recordData.posts.forEach(({ data }) => this.setVotesViewTypes(data));
      }

      const votesViewTypes = Object.values(this.votesViewTypes);

      if (votesViewTypes.length) {
        this.sendNewReachGoal(
          `Опрос ${votesViewTypes.join(' + ')}`,
          { Открыл: this.recordData.id },
          'VotesView'
        );
      }
    },
    sendNewReachGoal(fieldType, valueName, goalName) {
      const blockType = 'Центральная колонка';

      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName
      });
    },
    sendNewFormatReachGoal(goalName, blockType, actionType, prop1, prop2) {
      this.store.analyticsModule.sendNewFormatReachGoal({
        goalName,
        blockType,
        actionType,
        prop1,
        prop2
      });
    },
    clickRelatedNewsBtn() {
      this.sendNewReachGoal(DEFAULT_FIELD_TYPE, 'Показать еще', 'clickSameThemeBlock');
      this.sendNewFormatReachGoal('ClickOnThisTopic', 'По теме', 'Клик', 'Показать еще');
      this.isClickedRelatedNewsCounter = true;
    },
    handleClick(clickEvent) {
      if (clickEvent?.target?.nodeName !== 'A') {
        return;
      }
      const linkUrl = clickEvent?.target?.href || '';
      const blockType = 'Центральная колонка';
      const fieldType = 'Ссылка внутри текста';
      const valueName = { ['Клик']: linkUrl };
      const goalName = 'ClickURLfromText';
      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName
      });
    },
    handleHeaderClick(clickEvent) {
      if (this.isBroadcast || this.isCards) {
        this.handleClick(clickEvent);
      }
    },
    checkPointObsOptions(checkPoint) {
      return {
        callback: isVisible => this.checkPointVisible(isVisible, checkPoint),
        once: true,
        intersection: {
          threshold: 1
        }
      };
    },
    checkPointVisible(isVisible, checkPoint) {
      if (isVisible) {
        const fieldType = 'Скроллинг';
        const value = {
          [`Прочитал ${checkPoint}%`]: this.analyticRecordFormat
        };
        const goalName = `viewScroll${checkPoint}`;

        this.sendNewReachGoal(fieldType, value, goalName);
      }
    },
    nonIndexedVisible(isVisible) {
      if (isVisible && this.isNonIdexedWithLink) {
        const blockType = 'Центральная колонка';
        const fieldType = 'Неиндексируемый текст (блок)';
        const valueName = 'Просмотр';
        const goalName = 'viewNonIndexed';
        this.store.analyticsModule.sendNewReachGoal({
          blockType,
          fieldType,
          valueName,
          goalName
        });
      }
    },
    checkOpenArticle() {
      const fieldType = 'Скроллинг';
      const value = {
        ['Открыл']: this.analyticRecordFormat
      };
      const goalName = 'viewOpen';

      this.sendNewReachGoal(fieldType, value, goalName);
    },
    handleNonIndexedClick(clickEvent) {
      if (clickEvent?.target?.nodeName !== 'A') {
        return;
      }
      const linkUrl = clickEvent?.target?.href || '';
      const blockType = 'Центральная колонка';
      const fieldType = 'Неиндексируемый текст (блок)';
      const valueName = { ['Клик']: linkUrl };
      const goalName = 'clickNonIndexed';
      this.store.analyticsModule.sendNewReachGoal({
        blockType,
        fieldType,
        valueName,
        goalName
      });
    }
  }
};
</script>

<style lang="scss" module>
@import 'styles';

.recordBlock {
  display: block;

  background: $color-surface;
}

.articleContent {
  margin-bottom: 20px;
}

.articleContentText {
  position: relative;
}

.checkPoint {
  position: absolute;
  left: 0;
  width: 0px;
  height: 0px;
}
</style>
